// Coarse statement-kind classifier used by DocumentStructure to populate
// `StatementInfo.kind` before the Statement Classification slice lands full
// tree-based classification. First-token matching only — strips leading
// whitespace + line/block comments, then checks prefixes.

import type { StatementKind } from '../model';

// Strip leading whitespace + line/block comments so a classifier sees the first
// operative token. Exported so `schemaEffects.ts` reuses this exact stripper
// rather than copying it (the two helpers must agree on what "leading trivia" is).
export function stripLeadingCommentsAndWs(text: string): string {
  let head = text;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const trimmed = head.trimStart();
    if (trimmed.startsWith('--')) {
      const nl = trimmed.indexOf('\n');
      head = nl >= 0 ? trimmed.slice(nl + 1) : '';
      continue;
    }
    if (trimmed.startsWith('/*')) {
      const end = trimmed.indexOf('*/');
      head = end >= 0 ? trimmed.slice(end + 2) : '';
      continue;
    }
    return trimmed;
  }
}

// A WITH-led statement is *not* necessarily a SELECT: PG/MSSQL allow a CTE
// list to front a data-modifying `INSERT`/`UPDATE`/`DELETE`/`MERGE`. Treating
// every `WITH …` as `'select'` (the old behaviour) made a destructive
// `WITH x AS (…) DELETE FROM t` look like a harmless read to risk gating —
// data-loss risk. So skip past the CTE definition list to the operative
// top-level keyword and classify by THAT. The scan is parenthesis-balanced
// and aware of `'…'`/`"…"` literals + SQL comments, so a `)`/keyword inside an
// ordinary CTE body or a quoted literal can't throw it off. Known residual
// (NOT handled, by design — see the `skipQuoted` caveat): PG dollar-quoted
// bodies (`$$…$$`) and MSSQL `[bracket]` identifiers with a `)` can still
// unbalance the scan, but that only degrades toward `'unknown'`, never toward
// making a destructive statement look like a read. If the tail can't be
// resolved unambiguously we return `'unknown'` — never a `'select'` fallback,
// because mislabeling a destructive statement as a read is the bug being fixed.
function classifyWithLed(text: string): StatementKind {
  // `text` starts with `WITH` (comment/WS already stripped). Skip the keyword
  // and the optional `RECURSIVE`, then walk CTE entries until, at paren depth
  // 0 and outside any string, we hit the operative keyword after a CTE body.
  let i = 4; // past "WITH"
  const n = text.length;

  // Advance past whitespace and SQL comments (line + block) from position i.
  const skipTrivia = (): boolean => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      while (i < n && /\s/.test(text[i])) i++;
      if (text.startsWith('--', i)) {
        const nl = text.indexOf('\n', i);
        if (nl < 0) { i = n; return false; }
        i = nl + 1;
        continue;
      }
      if (text.startsWith('/*', i)) {
        const end = text.indexOf('*/', i);
        if (end < 0) { i = n; return false; }
        i = end + 2;
        continue;
      }
      return i < n;
    }
  };

  // Skip a single-quoted ('') or double-quoted ("") token, honouring SQL's
  // doubled-quote escape. Returns false if the string is unterminated.
  // Caveat: PG dollar-quoted strings (`$$ … $$` / `$tag$ … $tag$`) and MSSQL
  // `[bracket]` identifiers are intentionally NOT parsed here — a known,
  // fail-safe-direction limitation of this coarse classifier (an unbalanced
  // `)` inside one degrades toward `'unknown'`, never toward read-looks-safe).
  const skipQuoted = (q: string): boolean => {
    i++; // opening quote
    while (i < n) {
      if (text[i] === q) {
        if (text[i + 1] === q) { i += 2; continue; } // escaped quote
        i++;
        return true;
      }
      i++;
    }
    return false; // unterminated
  };

  // Read the next bare keyword/identifier (letters only — enough to spot
  // RECURSIVE / AS / NOT / MATERIALIZED / the operative DML/SELECT keyword).
  const peekWord = (): string => {
    let j = i;
    while (j < n && /[A-Za-z_]/.test(text[j])) j++;
    return text.slice(i, j).toUpperCase();
  };

  if (!skipTrivia()) return 'unknown';
  if (peekWord() === 'RECURSIVE') {
    i += 'RECURSIVE'.length;
  }

  // Walk CTE entries: `name [ (cols) ] AS [NOT] [MATERIALIZED] ( body ) [, …]`.
  // We don't need to validate the grammar — only to skip each parenthesised
  // CTE body so the first top-level keyword *after* the last body is the
  // operative statement keyword.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (!skipTrivia()) return 'unknown';
    // A CTE entry can have TWO depth-0 paren groups: an OPTIONAL column list
    // then the MANDATORY body. Skip to the *body*, not the first group: when a
    // group closes at depth 0, peek the next token — `AS`/`NOT`/`MATERIALIZED`
    // /`(` means the body is still ahead (just closed = column list), keep
    // scanning; a `,` or the operative keyword means that group WAS the body.
    // Strings/comments are skipped so a `)` in a literal can't unbalance us.
    let depth = 0;
    let sawBody = false;
    // eslint-disable-next-line no-constant-condition
    while (i < n) {
      const c = text[i];
      if (c === '-' && text[i + 1] === '-') {
        const nl = text.indexOf('\n', i);
        if (nl < 0) return 'unknown';
        i = nl + 1;
        continue;
      }
      if (c === '/' && text[i + 1] === '*') {
        const end = text.indexOf('*/', i);
        if (end < 0) return 'unknown';
        i = end + 2;
        continue;
      }
      if (c === "'" || c === '"') {
        if (!skipQuoted(c)) return 'unknown';
        continue;
      }
      if (c === '(') { depth++; i++; continue; }
      if (c === ')') {
        depth--;
        i++;
        if (depth === 0) {
          // Peek (without consuming) to tell the optional column list apart
          // from the body — see the block comment above for the rule.
          const save = i;
          if (!skipTrivia()) return 'unknown';
          const next = peekWord();
          if (next === 'AS' || next === 'NOT' || next === 'MATERIALIZED' || text[i] === '(') {
            i = save; // restore: we have not consumed the body yet
            continue;
          }
          i = save; // restore for the shared post-body trivia/branch below
          sawBody = true;
          break;
        }
        continue;
      }
      i++;
    }
    if (!sawBody || depth !== 0) return 'unknown'; // unterminated CTE body

    if (!skipTrivia()) return 'unknown';
    if (text[i] === ',') { i++; continue; } // another CTE follows

    // First token after the final CTE body is the operative keyword.
    const kw = peekWord();
    if (kw === 'INSERT') return 'insert';
    if (kw === 'UPDATE') return 'update';
    if (kw === 'DELETE') return 'delete';
    if (kw === 'MERGE') return 'merge';
    if (kw === 'SELECT' || kw === 'VALUES') return 'select';
    return 'unknown'; // unrecognised tail — never assume 'select'
  }
}

export function coarseKind(text: string): StatementKind {
  const stripped = stripLeadingCommentsAndWs(text);
  const upper = stripped.slice(0, 16).toUpperCase();
  if (upper.startsWith('SELECT')) return 'select';
  // WITH needs the full (stripped) text, not the 16-char head, to find the
  // operative keyword past the CTE list.
  if (upper.startsWith('WITH')) return classifyWithLed(stripped);
  if (upper.startsWith('INSERT')) return 'insert';
  if (upper.startsWith('UPDATE')) return 'update';
  if (upper.startsWith('DELETE')) return 'delete';
  if (upper.startsWith('MERGE')) return 'merge';
  if (upper.startsWith('CREATE')) return 'ddl-create';
  if (upper.startsWith('ALTER')) return 'ddl-alter';
  if (upper.startsWith('DROP')) return 'ddl-drop';
  if (upper.startsWith('TRUNCATE')) return 'ddl-truncate';
  if (upper.startsWith('RENAME')) return 'ddl-rename';
  if (upper.startsWith('GRANT')) return 'grant';
  if (upper.startsWith('REVOKE')) return 'revoke';
  if (upper.startsWith('COMMIT') || upper.startsWith('ROLLBACK') || upper.startsWith('SAVEPOINT')) return 'transaction-control';
  // `BEGIN` is transaction-control ONLY as `BEGIN TRAN(SACTION)`/`WORK` or a bare `BEGIN;`/`BEGIN`<EOL>; any other `BEGIN…` (`BEGIN DECLARE…`, `BEGIN TRY`) is an anonymous procedural block → `'procedural-body'` — this is the sole classifier for that distinction (the standalone procedural-batch classifier explored in Plan 16 T9 was deleted as unwired; `coarseKind` alone drives production classification). `\bBEGIN\b` keeps `BEGINNING` out.
  if (/^BEGIN\b/i.test(stripped)) return /^BEGIN(\s+(TRAN(SACTION)?|WORK)\b|\s*;|\s*$)/i.test(stripped) ? 'transaction-control' : 'procedural-body';
  // PG anonymous procedural block: `DO [LANGUAGE x] $$…$$ | $tag$…$tag$ | '…'`.
  // Gated on a dollar-quoted / quoted body so a MySQL `DO <expr>` statement
  // (e.g. `DO SLEEP(5)`) isn't mis-read as a block, and `\bDO\b` keeps `DOMAIN…`
  // out. Routes to `'procedural-body'` → the honest procedural read-only card
  // instead of the "unknown + Report-issue" costume (EDG-05).
  if (/^DO\b\s*(?:LANGUAGE\s+[A-Za-z_]\w*\s+)?(?:\$[A-Za-z_0-9]*\$|[EeNn]?')/i.test(stripped)) return 'procedural-body';
  // Oracle anonymous PL/SQL block: `DECLARE … BEGIN … END;`. Requires a
  // following `BEGIN` (the block body) and excludes the T-SQL `DECLARE @var`
  // sigil so an ordinary batch variable/cursor declaration — which has no
  // anonymous-block semantics — is NOT down-routed to read-only (EDG-05).
  if (/^DECLARE\b/i.test(stripped) && !/^DECLARE\b\s*@/i.test(stripped) && /\bBEGIN\b/i.test(stripped)) return 'procedural-body';
  if (upper.startsWith('SET ') || upper.startsWith('USE ')) return 'session-setting';
  if (upper.startsWith('EXPLAIN')) return 'explain';
  if (upper.startsWith('CALL ') || upper.startsWith('EXEC') || upper.startsWith('EXECUTE')) return 'call';
  if (upper.startsWith('PRAGMA')) return 'pragma';
  if (upper.startsWith('DELIMITER')) return 'client-directive';
  return 'unknown';
}
